/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file driverTracking.js
 * @description
 * Live GPS tracking of the courier **while a route is `inProgress`** — and only
 * then. A background `expo-location` task (Android foreground service / iOS
 * background location) records fixes into an AsyncStorage buffer; the buffer is
 * flushed to `POST /driver/routes/{id}/locations` in batches of up to 200 points
 * whenever the network allows, with exponential back-off on failures.
 *
 * Lifecycle (see also `useDriverTracking`):
 *  - start: route start, or resume on app launch / screen open when the route is
 *    `inProgress` and the location permission is granted;
 *  - stop: route finish / cancel / unassign push, a 409 `ROUTE_NOT_ACTIVE` or
 *    403 `TRACKING_DISABLED` answer, and logout.
 *
 * **Entry point:** `TaskManager.defineTask` runs at module top level and this
 * file is imported from `index.js`, so the task is registered on a headless
 * background start too (before React mounts). The task body never throws.
 *
 * @module helpers/driverTracking
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import {Platform} from 'react-native';
import * as Location from 'expo-location';
import * as TaskManager from 'expo-task-manager';

/** Background task name registered with expo-task-manager. */
export const DRIVER_TRACKING_TASK = 'happy-driver-route-tracking';

/** AsyncStorage key: `{routeId, settings, startedAt}` of the route being tracked. */
export const ACTIVE_ROUTE_KEY = 'HG_DRIVER_TRACKING_ROUTE';

/** AsyncStorage key: the buffered, not yet uploaded points. */
export const BUFFER_KEY = 'HG_DRIVER_TRACKING_BUFFER';

/** AsyncStorage key: the user accepted the prominent location disclosure. */
export const DISCLOSURE_KEY = 'HG_DRIVER_LOCATION_DISCLOSURE_ACCEPTED';

/** Most points kept offline; the oldest are dropped first. */
export const BUFFER_CAP = 2000;

/** Server-side maximum points per upload. */
export const BATCH_SIZE = 200;

/** Points older than this are dropped (the server drops them too). */
export const MAX_POINT_AGE_MS = 24 * 60 * 60 * 1000;

/** First retry delay and its ceiling after failed uploads. */
export const BACKOFF_BASE_MS = 5000;
export const BACKOFF_MAX_MS = 5 * 60 * 1000;

/** Backend settings used until / unless `GET /driver/settings` answers. */
export const DEFAULT_TRACKING_SETTINGS = Object.freeze({
    trackingEnabled: true,
    trackingIntervalSeconds: 20,
    trackingDistanceMeters: 50,
    arrivalRadiusMeters: 100,
    locationRetentionDays: null,
});

/** Error codes that mean "stop tracking this route". */
export const STOP_ERROR_CODES = ['ROUTE_NOT_ACTIVE', 'TRACKING_DISABLED'];

let flushing = false;
let failures = 0;
let nextAttemptAt = 0;

/** Lazily required so the headless task does not pull the whole networking tree at import time. */
const driverApi = () => require('./driverApi');

/**
 * Merge a settings object over the defaults, coercing the numbers.
 * @param {?object} settings Backend settings.
 * @returns {object} Complete settings.
 */
export const normalizeSettings = (settings) => {
    const num = (value, fallback) => (Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : fallback);
    const base = DEFAULT_TRACKING_SETTINGS;

    return {
        trackingEnabled: settings?.trackingEnabled !== false,
        trackingIntervalSeconds: num(settings?.trackingIntervalSeconds, base.trackingIntervalSeconds),
        trackingDistanceMeters: num(settings?.trackingDistanceMeters, base.trackingDistanceMeters),
        arrivalRadiusMeters: num(settings?.arrivalRadiusMeters, base.arrivalRadiusMeters),
        locationRetentionDays: num(settings?.locationRetentionDays, null),
    };
};

const readJson = async (key, fallback) => {
    try {
        const raw = await AsyncStorage.getItem(key);

        return raw ? JSON.parse(raw) : fallback;
    } catch (_error) {
        return fallback;
    }
};

const writeJson = async (key, value) => {
    try {
        await AsyncStorage.setItem(key, JSON.stringify(value));
    } catch (_error) {
        // Storage full / unavailable: tracking must never crash.
    }
};

/**
 * Convert an expo-location fix into an upload point.
 * @param {object} location `LocationObject`.
 * @returns {?object} `{lat, lng, accuracy, speed, heading, altitude, recordedAt}` or null when unusable.
 */
export const toPoint = (location) => {
    const coords = location?.coords;
    const lat = Number(coords?.latitude);
    const lng = Number(coords?.longitude);

    if (!coords || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
    }

    const num = (value) => (value === null || value === undefined || !Number.isFinite(Number(value)) ? undefined : Number(value));
    const nonNegative = (value) => (num(value) !== undefined && num(value) >= 0 ? num(value) : undefined);
    const stamp = Number(location.timestamp);

    return {
        lat,
        lng,
        accuracy: num(coords.accuracy),
        speed: nonNegative(coords.speed),
        heading: nonNegative(coords.heading),
        altitude: num(coords.altitude),
        recordedAt: new Date(Number.isFinite(stamp) && stamp > 0 ? stamp : Date.now()).toISOString(),
    };
};

/**
 * Append points to the offline buffer, dropping the oldest beyond {@link BUFFER_CAP}.
 * @param {Array<object>} points New points (oldest first).
 * @returns {Promise<number>} Buffer size afterwards.
 */
export const appendPoints = async (points) => {
    const fresh = (Array.isArray(points) ? points : []).filter(Boolean);

    if (!fresh.length) {
        return (await readJson(BUFFER_KEY, [])).length;
    }

    const merged = [...(await readJson(BUFFER_KEY, [])), ...fresh];
    const capped = merged.length > BUFFER_CAP ? merged.slice(merged.length - BUFFER_CAP) : merged;

    await writeJson(BUFFER_KEY, capped);

    return capped.length;
};

/**
 * Number of buffered points.
 * @returns {Promise<number>} Count.
 */
export const bufferSize = async () => (await readJson(BUFFER_KEY, [])).length;

/**
 * The persisted `{routeId, settings}` of the route being tracked.
 * @returns {Promise<?{routeId: string, settings: object, startedAt: string}>} Active record.
 */
export const getActiveTracking = async () => {
    const record = await readJson(ACTIVE_ROUTE_KEY, null);

    return record && record.routeId ? record : null;
};

/**
 * Whether an upload error means the server no longer wants points for the route.
 * @param {Error} error Rejected error.
 * @returns {boolean} True for 409 ROUTE_NOT_ACTIVE / 403 TRACKING_DISABLED.
 */
export const isStopTrackingError = (error) => {
    const {apiErrorCode, apiErrorStatus} = require('./apiError');
    const code = apiErrorCode(error);

    if (STOP_ERROR_CODES.includes(code)) {
        return true;
    }

    const status = apiErrorStatus(error);

    return (status === 409 && /ROUTE_NOT_ACTIVE/.test(String(error?.message || '')))
        || (status === 403 && /TRACKING_DISABLED/.test(String(error?.message || '')));
};

/**
 * Stop the background updates and forget the active route + buffered points.
 * @returns {Promise<void>} Resolves when done; never rejects.
 */
export const stopTracking = async () => {
    try {
        if (await Location.hasStartedLocationUpdatesAsync(DRIVER_TRACKING_TASK)) {
            await Location.stopLocationUpdatesAsync(DRIVER_TRACKING_TASK);
        }
    } catch (_error) {
        // Already stopped / task unknown.
    }
    try {
        await AsyncStorage.multiRemove([ACTIVE_ROUTE_KEY, BUFFER_KEY]);
    } catch (_error) {
        // Ignore.
    }
    failures = 0;
    nextAttemptAt = 0;
};

/**
 * Upload the buffer in batches of {@link BATCH_SIZE}. Serialised, honours the
 * back-off window, never throws. A stop-code answer ends tracking.
 * @param {{force?: boolean}} [options] `force` ignores the back-off window.
 * @returns {Promise<{sent: number, remaining: number, stopped: boolean}>} Outcome.
 */
export const flushBuffer = async ({force = false} = {}) => {
    const outcome = {sent: 0, remaining: 0, stopped: false};

    if (flushing) {
        outcome.remaining = await bufferSize();

        return outcome;
    }
    if (!force && Date.now() < nextAttemptAt) {
        outcome.remaining = await bufferSize();

        return outcome;
    }

    flushing = true;
    try {
        const active = await getActiveTracking();

        if (!active) {
            return outcome;
        }

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const cutoff = Date.now() - MAX_POINT_AGE_MS;
            const buffer = (await readJson(BUFFER_KEY, [])).filter((p) => Date.parse(p?.recordedAt) >= cutoff);
            const batch = buffer.slice(0, BATCH_SIZE);

            if (!batch.length) {
                await writeJson(BUFFER_KEY, buffer);
                break;
            }

            try {
                await driverApi().postRouteLocations(active.routeId, batch);
            } catch (error) {
                if (isStopTrackingError(error)) {
                    await stopTracking();
                    outcome.stopped = true;
                    outcome.remaining = 0;

                    return outcome;
                }
                failures += 1;
                nextAttemptAt = Date.now() + Math.min(BACKOFF_BASE_MS * (2 ** (failures - 1)), BACKOFF_MAX_MS);
                outcome.remaining = buffer.length;

                return outcome;
            }

            // Re-read: the task may have appended while the request was in flight.
            const latest = (await readJson(BUFFER_KEY, [])).filter((p) => Date.parse(p?.recordedAt) >= cutoff);
            const rest = latest.slice(batch.length);

            await writeJson(BUFFER_KEY, rest);
            outcome.sent += batch.length;
            failures = 0;
            nextAttemptAt = 0;
            if (!rest.length) {
                break;
            }
        }
        outcome.remaining = await bufferSize();
    } catch (_error) {
        outcome.remaining = await bufferSize().catch(() => 0);
    } finally {
        flushing = false;
    }

    return outcome;
};

/**
 * Body of the background task: buffer the fixes, then try to flush. Exported for
 * tests; never throws.
 * @param {{data?: {locations?: Array<object>}, error?: ?object}} payload Task payload.
 * @returns {Promise<void>} Resolves when done.
 */
export const handleLocationTask = async ({data, error} = {}) => {
    try {
        if (error || !data) {
            return;
        }
        if (!(await getActiveTracking())) {
            // Orphaned task (route already stopped): shut it down.
            await stopTracking();

            return;
        }
        await appendPoints((data.locations || []).map(toPoint));
        await flushBuffer();
    } catch (_error) {
        // Background tasks must never throw.
    }
};

try {
    TaskManager.defineTask(DRIVER_TRACKING_TASK, handleLocationTask);
} catch (_error) {
    // Web / unsupported runtime: tracking is simply unavailable.
}

/**
 * Whether the user already accepted the prominent disclosure.
 * @returns {Promise<boolean>} True when accepted.
 */
export const isDisclosureAccepted = async () => (await readJson(DISCLOSURE_KEY, false)) === true;

/**
 * Remember that the prominent disclosure was accepted.
 * @returns {Promise<void>} Resolves when stored.
 */
export const markDisclosureAccepted = async () => writeJson(DISCLOSURE_KEY, true);

/**
 * Current location permission state.
 * @returns {Promise<{foreground: boolean, background: boolean, canAskAgain: boolean}>} State (all false when unavailable).
 */
export const getPermissionState = async () => {
    try {
        const fg = await Location.getForegroundPermissionsAsync();
        // Android: no background permission exists (blocked) — the foreground
        // service covers screen-off recording, so it mirrors the foreground grant.
        const bg = !fg.granted ? {granted: false}
            : Platform.OS === 'android' ? {granted: true}
                : await Location.getBackgroundPermissionsAsync();

        return {foreground: !!fg.granted, background: !!bg.granted, canAskAgain: fg.canAskAgain !== false};
    } catch (_error) {
        return {foreground: false, background: false, canAskAgain: false};
    }
};

/**
 * Ask for the location permissions: foreground first, then background (only if
 * the foreground one was granted). Call this ONLY after the user accepted the
 * prominent disclosure.
 *
 * Android never asks for background location: `ACCESS_BACKGROUND_LOCATION` is
 * blocked (Google Play background-location policy) — the location foreground
 * service started from the visible route screen keeps recording with the
 * screen off on the "while using the app" grant alone.
 * @returns {Promise<{foreground: boolean, background: boolean, canAskAgain: boolean}>} Resulting state.
 */
export const requestTrackingPermissions = async () => {
    try {
        const fg = await Location.requestForegroundPermissionsAsync();

        if (fg.granted && Platform.OS !== 'android') {
            await Location.requestBackgroundPermissionsAsync();
        }
    } catch (_error) {
        // Fall through to the state read-back.
    }

    return getPermissionState();
};

/**
 * Start (or restart with new settings) background tracking of a route.
 * Needs at least the foreground permission; the background grant lets it keep
 * recording with the screen off.
 * @param {string} routeId Route plan id (must be `inProgress`).
 * @param {?object} [settings] Backend tracking settings.
 * @returns {Promise<{started: boolean, reason?: string}>} `reason`: no-route | disabled | no-permission | error.
 */
export const startTracking = async (routeId, settings = null) => {
    if (!routeId) {
        return {started: false, reason: 'no-route'};
    }

    const config = normalizeSettings(settings);

    if (!config.trackingEnabled) {
        return {started: false, reason: 'disabled'};
    }

    try {
        const permission = await getPermissionState();

        if (!permission.foreground) {
            return {started: false, reason: 'no-permission'};
        }

        const active = await getActiveTracking();
        const running = await Location.hasStartedLocationUpdatesAsync(DRIVER_TRACKING_TASK);

        if (running && active && active.routeId === String(routeId)
            && active.settings?.trackingIntervalSeconds === config.trackingIntervalSeconds
            && active.settings?.trackingDistanceMeters === config.trackingDistanceMeters) {
            return {started: true};
        }
        if (running) {
            await Location.stopLocationUpdatesAsync(DRIVER_TRACKING_TASK);
        }
        if (active && active.routeId !== String(routeId)) {
            // A different route's leftovers must not be uploaded to this one.
            await AsyncStorage.removeItem(BUFFER_KEY);
        }

        await writeJson(ACTIVE_ROUTE_KEY, {routeId: String(routeId), settings: config, startedAt: new Date().toISOString()});
        await Location.startLocationUpdatesAsync(DRIVER_TRACKING_TASK, {
            accuracy: Location.Accuracy.High,
            timeInterval: config.trackingIntervalSeconds * 1000,
            distanceInterval: config.trackingDistanceMeters,
            showsBackgroundLocationIndicator: true,
            pausesUpdatesAutomatically: false,
            activityType: Location.ActivityType?.AutomotiveNavigation,
            foregroundService: {
                notificationTitle: 'Útvonal követése',
                notificationBody: 'A Happy App a kiszállítási útvonal alatt rögzíti a pozíciódat.',
                notificationColor: '#F97316',
            },
        });

        return {started: true};
    } catch (_error) {
        await AsyncStorage.removeItem(ACTIVE_ROUTE_KEY).catch(() => {});

        return {started: false, reason: 'error'};
    }
};

/**
 * Whether the background task is currently running.
 * @returns {Promise<boolean>} True when tracking.
 */
export const isTracking = async () => {
    try {
        return await Location.hasStartedLocationUpdatesAsync(DRIVER_TRACKING_TASK);
    } catch (_error) {
        return false;
    }
};

/**
 * Stop tracking when the active route matches (or always, with no id).
 * @param {?string} [routeId] Route that ended; omit to stop whatever is tracked.
 * @returns {Promise<boolean>} True when something was stopped.
 */
export const stopTrackingForRoute = async (routeId = null) => {
    const active = await getActiveTracking();

    if (routeId && active && active.routeId !== String(routeId)) {
        return false;
    }
    if (!active && !(await isTracking())) {
        return false;
    }
    // Best-effort last upload before the buffer is discarded.
    await flushBuffer({force: true});
    await stopTracking();

    return true;
};

/**
 * Logout hook: upload what is left (bounded wait) and shut tracking down.
 * @param {number} [waitMs] Longest wait for the final upload.
 * @returns {Promise<void>} Resolves when tracking is off; never rejects.
 */
export const stopTrackingOnLogout = async (waitMs = 3000) => {
    try {
        await Promise.race([flushBuffer({force: true}), new Promise((resolve) => globalThis.setTimeout(resolve, waitMs))]);
    } catch (_error) {
        // Ignore: logout must proceed.
    }
    await stopTracking();
};

/**
 * Whether `Platform` supports background tracking at all.
 * @returns {boolean} True on iOS and Android.
 */
export const isTrackingSupported = () => Platform.OS === 'ios' || Platform.OS === 'android';

/**
 * Resume tracking on app launch: when a route is `inProgress` for the signed-in
 * courier and permission + disclosure are already in place. Silent — it never
 * prompts. Stops a stale task whose route is no longer in progress.
 * @returns {Promise<{resumed: boolean, routeId?: string}>} Outcome.
 */
export const resumeTrackingIfNeeded = async () => {
    try {
        if (!isTrackingSupported()) {
            return {resumed: false};
        }

        const api = driverApi();
        const active = await getActiveTracking();

        if (active) {
            const route = await api.fetchDriverRoute(active.routeId).catch((error) => (isStopTrackingError(error) ? null : undefined));

            if (route === null || (route && route.executionStatus !== 'inProgress')) {
                await stopTrackingForRoute(active.routeId);

                return {resumed: false};
            }
            if (route === undefined) {
                return {resumed: false};
            }
        }

        const permission = await getPermissionState();

        if (!permission.foreground || !(await isDisclosureAccepted())) {
            return {resumed: false};
        }

        let routeId = active?.routeId;

        if (!routeId) {
            const today = new Date().toISOString().slice(0, 10);
            const routes = await api.fetchDriverRoutes({from: today, to: today});
            const running = (Array.isArray(routes) ? routes : []).find((r) => r.executionStatus === 'inProgress');

            routeId = running?._id;
        }
        if (!routeId) {
            return {resumed: false};
        }

        const settings = await api.fetchDriverSettings().catch(() => null);
        const result = await startTracking(routeId, settings);

        if (result.started) {
            await flushBuffer();
        }

        return {resumed: result.started, routeId};
    } catch (_error) {
        return {resumed: false};
    }
};

/**
 * Subscribe to the driver push bus so a route cancel / unassign push stops
 * tracking at once. Safe to call repeatedly (returns the same unsubscribe).
 * @returns {() => void} Unsubscribe.
 */
export const watchRoutePushes = () => {
    const {observeDriverPush} = require('./driverPushBus');
    const {DRIVER_EVENT_KEYS} = require('./driverPushPayload');

    return observeDriverPush((payload) => {
        const key = payload?.eventKey;

        if (key === DRIVER_EVENT_KEYS.routeCancelled || key === DRIVER_EVENT_KEYS.routeUnassigned) {
            stopTrackingForRoute(payload.routePlanId || null).catch(() => {});
        }
    });
};

/** Reset module state between tests. */
export const __resetForTests = () => {
    flushing = false;
    failures = 0;
    nextAttemptAt = 0;
};

export default {
    startTracking,
    stopTracking,
    stopTrackingForRoute,
    flushBuffer,
    resumeTrackingIfNeeded,
};
